/**
 * ChatController — manages text chat messages (send + receive).
 *
 * Lit Reactive Controller equivalent of useRinggChat from React Native.
 */

import type { ReactiveController, ReactiveControllerHost } from "lit";

import type { RealtimeRoomAdapter, ConnectionState, ChatMessage } from "@ringg/core";
import { isAgentIdentity } from "@ringg/core";

/** Topic the backend uses for agent text replies (protocol constant). */
const TRANSCRIPTION_TOPIC = "lk.transcription";

export class ChatController implements ReactiveController {
  host: ReactiveControllerHost;

  private adapter: RealtimeRoomAdapter;
  private cleanupFns: Array<() => void> = [];
  private connState: ConnectionState = "disconnected";
  private streamRegistered = false;

  messages: ChatMessage[] = [];

  /** Set externally by ComponentController when source URLs arrive from RPC. */
  sourceUrl = "";

  constructor(host: ReactiveControllerHost, adapter: RealtimeRoomAdapter) {
    this.host = host;
    this.adapter = adapter;
    host.addController(this);
  }

  hostConnected(): void {
    const unsub = this.adapter.onConnectionStateChange((state) => {
      const prev = this.connState;
      this.connState = state;

      if (state === "connected" && prev !== "connected") {
        this._registerTextStream();
      } else if (state !== "connected" && this.streamRegistered) {
        this._unregisterTextStream();
      }
    });

    this.cleanupFns.push(unsub);

    // Check initial state
    this.connState = this.adapter.getConnectionState();
    if (this.connState === "connected") {
      this._registerTextStream();
    }
  }

  hostDisconnected(): void {
    this._unregisterTextStream();
    this.cleanupFns.forEach((fn) => fn());
    this.cleanupFns = [];
  }

  async sendMessage(text: string): Promise<void> {
    if (!text.trim()) return;

    // Optimistic local message
    const localMsg: ChatMessage = {
      message: text,
      isSelf: true,
      name: "You",
      timestamp: Date.now(),
    };

    this.messages = [...this.messages, localMsg];
    this.host.requestUpdate();

    try {
      await this.adapter.sendChatMessage(text);
    } catch {
      // Send failure is non-fatal — message already shown optimistically
    }
  }

  addComponentMessage(message: ChatMessage): void {
    this.messages = [...this.messages, message];
    this.host.requestUpdate();
  }

  clearMessages(): void {
    this.messages = [];
    this.host.requestUpdate();
  }

  private _registerTextStream(): void {
    // Unregister stale handler first
    this._unregisterTextStream();

    try {
      this.adapter.registerTextStreamHandler(TRANSCRIPTION_TOPIC, (info) => {
        const isAgent = isAgentIdentity(info.participantIdentity);

        const msg: ChatMessage = {
          message: info.message,
          isSelf: !isAgent,
          name: isAgent ? "Agent" : "You",
          timestamp: Date.now() + Math.random() * 0.001,
        };

        // Attach source URL to agent messages (matching CDN behavior)
        if (isAgent && this.sourceUrl) {
          msg.sourceUrl = this.sourceUrl;
          this.sourceUrl = ""; // consume (one-shot)
        }

        this._mergeMessage(msg);
      });
      this.streamRegistered = true;
    } catch {
      // Registration may fail if room is disconnecting
    }
  }

  private _unregisterTextStream(): void {
    if (!this.streamRegistered) return;
    try {
      this.adapter.unregisterTextStreamHandler(TRANSCRIPTION_TOPIC);
    } catch {
      // no-op
    }
    this.streamRegistered = false;
  }

  private _mergeMessage(msg: ChatMessage): void {
    const seen = new Set<number>();
    this.messages = [...this.messages, msg].filter((m) => !seen.has(m.timestamp) && seen.add(m.timestamp)).sort((a, b) => a.timestamp - b.timestamp);
    this.host.requestUpdate();
  }
}
